import type { InputFileAudio } from "@/client/core/Core.api";
import { createInputAudioType } from "@/client/utils/createInputAudioType";
import { fetchAudioAsFile } from "@/client/utils/fetchAudioAsFile";
import { toChordType, toMusicalKey } from "@/shared/musical-key.api";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

/**
 * The name the stored resource gets. Catalog names arrive as paths
 * (`Pack/Drums/kick_01.wav`); the last segment without its extension is what
 * the plugin shows and what a filename should be.
 */
export const getSpliceSampleDisplayName = (name: string): string => {
    const segments = name.split("/");
    const fileName = segments[segments.length - 1] ?? name;
    return fileName.replace(/\.\w+$/, "");
};

/** Fetches a catalog sample's preview as a File named after the sample. */
export const fetchSpliceSampleFile = (result: SpliceSearchResult): Promise<File> =>
    fetchAudioAsFile(result.previewUrl, getSpliceSampleDisplayName(result.metadata.name));

/**
 * Pairs a fetched preview with the catalog's own description of it, so the
 * resource carries BPM, key, tags, artwork and the catalog id from the start
 * rather than being analysed as if it were a user upload.
 */
export const createSpliceSampleInputFile = (
    result: SpliceSearchResult,
    file: File,
    /** Tags to record beyond the catalog's own, such as a marker for a transient resource. */
    extraTags: readonly string[] = [],
): InputFileAudio => {
    const { metadata } = result;
    return {
        file,
        type: "wav",
        metadata: {
            spliceUuid: metadata.uuid,
            bpm: metadata.bpm > 0 ? metadata.bpm : null,
            key: toMusicalKey(metadata.key) ?? null,
            chordType: toChordType(metadata.chordType) ?? null,
            artworkUrl: metadata.packArtworkUrl !== "" ? metadata.packArtworkUrl : null,
            tags: [...metadata.tags, ...extraTags],
            type: createInputAudioType(metadata.assetCategory),
        },
    };
};
